// Typo-Bauteile (DESIGN §2.4, §3.17): Rollen als Props, Farbe über `tone`, Zahlen über Intl.
import { type ComponentPropsWithRef, type ElementType, type ReactNode } from 'react';
import { cn } from './cn';
import { cva, type VariantProps } from './cva';
import { formatDelta, formatMoney, formatMultiplier, formatNumber, formatPercent } from './format';

export const textVariants = cva('', {
  variants: {
    variant: {
      'kpi-xl': 'text-kpi-xl',
      display: 'text-display',
      'kpi-l': 'text-kpi-l',
      'title-page': 'text-title-page-m md:text-title-page',
      'title-profile': 'text-title-profile',
      'value-section': 'text-value-section',
      'value-grade': 'text-value-grade',
      'title-detail': 'text-title-detail',
      kpi: 'text-kpi',
      counter: 'text-counter',
      heading: 'text-heading',
      'label-strong': 'text-label-strong',
      body: 'text-body',
      prose: 'text-prose',
      meta: 'text-meta',
      button: 'text-button',
      label: 'text-label',
      small: 'text-small',
      chip: 'text-chip',
      nav: 'text-nav',
      mono: 'font-mono text-mono',
    },
    tone: {
      default: 'text-fg',
      secondary: 'text-fg-secondary',
      tertiary: 'text-fg-tertiary',
      accent: 'text-accent-fg',
      success: 'text-success-fg',
      warning: 'text-warning-fg',
      danger: 'text-danger-fg',
      ai: 'text-ai-fg',
      inherit: '',
    },
    weight: {
      regular: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
    },
    truncate: { true: 'truncate', false: '' },
    clamp: { 1: 'line-clamp-1', 2: 'line-clamp-2', 3: 'line-clamp-3' },
  },
  defaultVariants: { variant: 'body', tone: 'default' },
});

type TextVariants = VariantProps<typeof textVariants>;

export type TextProps<E extends ElementType = 'span'> = {
  as?: E;
  children?: ReactNode;
} & TextVariants &
  Omit<ComponentPropsWithRef<E>, 'as' | 'children' | keyof TextVariants>;

/** Fließ- und Beschriftungstext in einer Typo-Rolle. */
export function Text<E extends ElementType = 'span'>({
  as,
  variant,
  tone,
  weight,
  truncate,
  clamp,
  className,
  ...rest
}: TextProps<E>) {
  const Component: ElementType = as ?? 'span';
  return (
    <Component
      data-variant={variant ?? 'body'}
      className={textVariants({ variant, tone, weight, truncate, clamp, className })}
      {...rest}
    />
  );
}

export type HeadingLevel = 1 | 2 | 3 | 4;

export type HeadingProps = {
  level?: HeadingLevel;
  children?: ReactNode;
} & TextVariants &
  Omit<ComponentPropsWithRef<'h2'>, 'children' | keyof TextVariants>;

const HEADING_DEFAULT: Record<HeadingLevel, NonNullable<TextVariants['variant']>> = {
  1: 'title-page',
  2: 'title-detail',
  3: 'body',
  4: 'label-strong',
};

/** Echte Überschrift `h1`–`h4`; die Rolle folgt dem Level, kann aber überschrieben werden. */
export function Heading({
  level = 2,
  variant,
  tone,
  weight,
  truncate,
  clamp,
  className,
  ...rest
}: HeadingProps) {
  const Tag = `h${level}` as const;
  return (
    <Tag
      className={textVariants({
        variant: variant ?? HEADING_DEFAULT[level],
        tone,
        weight: weight ?? (level === 3 ? 'semibold' : undefined),
        truncate,
        clamp,
        className,
      })}
      {...rest}
    />
  );
}

export type NumFormat =
  | { kind: 'number'; digits?: number; compact?: boolean }
  | { kind: 'percent'; digits?: number }
  | { kind: 'money'; currency: string; compact?: boolean }
  | { kind: 'delta'; unit?: string; digits?: number }
  | { kind: 'multiplier' };

/** Zahl als Text nach §4.8 (für `Num` und Tabellen). */
export function formatNum(value: number, format: NumFormat = { kind: 'number' }): string {
  switch (format.kind) {
    case 'percent':
      return formatPercent(value, format.digits ?? 0);
    case 'money':
      return formatMoney(value, format.currency, { compact: format.compact });
    case 'delta':
      return formatDelta(value, format.unit, format.digits ?? 0);
    case 'multiplier':
      return formatMultiplier(value);
    default:
      return formatNumber(value, {
        maximumFractionDigits: format.digits ?? 2,
        minimumFractionDigits: format.digits ?? 0,
        ...(format.compact ? { notation: 'compact' } : {}),
      });
  }
}

export type NumProps = {
  value: number | null | undefined;
  format?: NumFormat;
  /** Anzeige, wenn kein Wert da ist (DESIGN §3.8.2 „—“). */
  fallback?: string;
} & TextVariants &
  Omit<ComponentPropsWithRef<'span'>, 'children' | keyof TextVariants>;

/** Kennzahl mit `tabular-nums`; `money` erwartet Minor Units. */
export function Num({
  value,
  format,
  fallback = '—',
  variant,
  tone,
  weight,
  className,
  ...rest
}: NumProps) {
  const missing = value === null || value === undefined || Number.isNaN(value);
  return (
    <span
      data-variant={variant ?? 'body'}
      className={cn(
        textVariants({ variant, tone: missing ? 'tertiary' : tone, weight }),
        'tabular-nums',
        className,
      )}
      {...rest}
    >
      {missing ? fallback : formatNum(value, format)}
    </span>
  );
}
